import { Text, Button, Icon, Badge } from '@artprov/design-system'
import { getIdCuenta } from './idCuenta'
import { formatearCuit } from '../../seleccion-cuenta/util/formatearCuit'
import { MEDICO_BADGE_STATE, puedeSubirDocumento } from '../constants/medicoEstados'

/**
 * Chip de estado de la cuenta: "Activo" (verde) o "Pendiente" (naranja).
 * @param {string} estado - Estado del contrato ("Activo" | "Pendiente")
 */
// Mismo estilo del chip «DDJJ pendiente» del menú Mi Perfil (UserProfileContent):
// variant="filled" sin size="sm", que es el que deja el texto pegado a los bordes.
const renderEstadoChip = (estado) => (
  <Badge variant="filled" state={estado === 'Activo' ? 'success' : 'warning'}>
    {estado}
  </Badge>
)

/**
 * Indicador del estado del perfil Médico de la cuenta (concepto distinto del
 * estado de la cuenta): punto de color + texto según el string del endpoint.
 * @param {string} medico - "Si" | "No" | "DDJJ pendiente" | "DDJJ pendiente de aprobación"
 */
const renderMedicoBadge = (medico) => (
  <Badge variant="dot" state={MEDICO_BADGE_STATE[medico] ?? 'neutral'}>
    {/* Fallback "No": el back puede no informar el campo (ej: cuentas viejas).
        Mismo comportamiento que tenía la columna antes del refactor a Badge. */}
    {medico || 'No'}
  </Badge>
)

export function buildCuentasRows(data, { onEliminar, onSubirDocumento } = {}) {
  if (!Array.isArray(data)) {
    return []
  }
  return data.map((item) => ({
    // Id único y estable por fila: el design-system usa este valor como
    // idTabla_DS para identificar cada fila. Los contratos se identifican
    // por `contrato` y las solicitudes pendientes (que pueden no tener
    // contrato) por `idSolicitud`. Nunca por posición: ids posicionales
    // cambian al filtrar/eliminar y causan filas duplicadas o borradas
    // incorrectamente al actualizar la grilla.
    id: getIdCuenta(item),

    RAZON_SOCIAL: (
      <Text variant="body2" className="text-primary-10 truncate">
        {item.razonSocial}
      </Text>
    ),

    CUIT: (
      <Text variant="body2" className="text-primary-10">
        {formatearCuit(item.cuit)}
      </Text>
    ),

    CONTRATO: (
      <Text variant="body2" className="text-primary-10">
        {item.contrato ?? '-'}
      </Text>
    ),

    ESTADO: renderEstadoChip(item.estado),

    ADMINISTRADOR: (
      <Text variant="body2" className="text-primary-10">
        {item.administrador ?? '-'}
      </Text>
    ),

    MEDICO: renderMedicoBadge(item.medico),

    SUBIR_DOCUMENTO: (
      <div className="flex justify-center">
        <Button
          aria-label={`Subir documento ${item.razonSocial ?? ''}`.trim()}
          iconOnly
          size="xs"
          variant="ghost"
          // Habilitado solo con DDJJ pendiente; en los otros estados queda
          // deshabilitado y con el ícono grisado. El gris va como estilo inline
          // porque el Button ghost pisa por cascada el color del Icon y la
          // variante "disabled" del Icon no aplica sobre un span.
          isDisabled={!puedeSubirDocumento(item.medico)}
          startIcon={
            <Icon
              icon="upload_file"
              size="small"
              style={
                puedeSubirDocumento(item.medico)
                  ? undefined
                  : { color: 'var(--ds-button-primary-text-disabled, #898887)' }
              }
            />
          }
          onClick={() => onSubirDocumento?.(item)}
        />
      </div>
    ),

    acciones: onEliminar ? (
      <Button
        aria-label={`Eliminar cuenta ${item.razonSocial ?? ''}`.trim()}
        iconOnly
        size="xs"
        variant="ghost"
        startIcon={<Icon icon="delete" size="small" />}
        onClick={() => onEliminar(item)}
      />
    ) : null,
  }))
}
